import React, { useState, useEffect } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, useTheme } from '@mui/material';
import { systemsIntegrationAxios as axios } from "../../../utils/AxiosUtility";
import EditInternalInfo from './EditInternalInfo';

export default function InternalInfoTable({id, type, userCanEdit}) {
  const borderStyle = { border: null };
  const externalBorderStyle = {borderTop: '2px solid #BEBEBE', borderRight: '2px solid #BEBEBE', borderBottom: '2px solid #BEBEBE', borderLeft: '2px solid #BEBEBE'};
  const [rowId, setRowId] = useState(id);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [internalInfo, setInternalInfo] = useState({});
  const [prodSummaryData, setProdSummaryData] = useState({});
  const [productInfo, setProductInfo] = useState({});
  const [siteInfoData, setSiteInfoData] = useState({});
  const [riserCount, setRiserCount] = useState();
  const [siteBuild, setSiteBuild] = useState();
  const [programmer, setProgrammer] = useState('');
  const [installTester, setInstallTester] = useState('');
  const [timerTest, setTimerTest] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const [productConfigData, setProductConfigData] = useState();
  const [dynamicHeight, setDynamicHeight] = useState('');
  const theme = useTheme();

  useEffect(() => {
    if (type == 'ED') {
      setDynamicHeight('500px');
    }
    else {
      setDynamicHeight('350px');
    }
  }, [type]);

  const fetchData = async () => {
    try {
      const internalInfoEndpoint = `api/InternalInfo/getDeviceLevelInternalInfo/${id}`;
      const response = await axios.get(internalInfoEndpoint);
      setInternalInfo(response.data);
      setIsLoading(false);
    }
    catch (error) {
      setIsLoading(false);
      alert("Error retrieving data"); 
    }
  };

  const fetchProductData = async () => {
    try {
      const productInfoEndpoint = `api/ProductSummary/getProductSummaryFromPcId/${id}`;
      const response = await axios.get(productInfoEndpoint);
      setProductInfo(response.data);
      setIsLoading(false);
    }
    catch (error) {
      setIsLoading(false);
      alert("Error retrieving data"); 
    }
  };

  const fetchProgrammer = async () => {
    try {
      const programmerFromPcEndpoint = `api/ProductConfiguration/productConfig/${id}`;
      const response = await axios.get(programmerFromPcEndpoint);
      setProgrammer(response.data.programmer);
      setInstallTester(response.data.installTester);
      setTimerTest(response.data.timerTest);
      setProductConfigData(response.data);
    }
    catch (error) {
      alert("Error fetching programmer.");
    }
  };

  const fetchProdSummary = async () => {
    try {
      const prodSummaryFromPcEndpoint = `api/ProductSummary/getProductSummaryFromPcId/${id}`;
      const response = await axios.get(prodSummaryFromPcEndpoint);
      setProdSummaryData(response.data);

    }
    catch (error) {
      alert("Error fetching prod summary info.");
    }
  };

  const fetchSiteInfo = async () => {
    try {
      const siteInfoEndpoint = `api/SiteInfo/getSiteInfo/${id}`;
      const response = await axios.get(siteInfoEndpoint);
      setSiteInfoData(response.data);
      setTimeZone(response.data.timeZone);
    }
    catch (error) {
      alert("Error fetching data.");
    }
  };

  const fetchTimeZone = async (prodId) => {
    try {
      if (!prodId) return;

      const timezoneEndpoint = `api/Product/getProduct/${prodId}`;
      const response = await axios.get(timezoneEndpoint);
      setTimeZone(response.data.unitTimeZone);
    }
    catch (error) {
      alert("Error fetching data.");
    }
  };

  useEffect(() => {
    fetchData();
    fetchProductData();
    fetchProgrammer();
    fetchProdSummary();
    fetchSiteInfo();
  }, [rowId]);

  useEffect(() => {
    fetchTimeZone(internalInfo.productId);
  }, [internalInfo.productId]);

  const handleInfoUpdate = async () => {
    setIsUpdating(true);
    try {
      await Promise.all([
        fetchData(),
        fetchProductData(),
        fetchProgrammer(),
        fetchProdSummary(),
        fetchSiteInfo()
      ]);
    } catch (error) {
      alert("Error updating info");
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    handleInfoUpdate();
  }, [rowId]);

  useEffect(() => {
    setRiserCount(JSON.stringify(productInfo.riserCount));
  }, [productInfo]);

  useEffect(() => {
    switch (riserCount) {
      case '1':
        setSiteBuild('Single');
        break;
      case '2':
        setSiteBuild('Double');
        break;
      case '3':
        setSiteBuild('Triple');
        break;
      case '4':
        setSiteBuild('Quad');
        break;
      default:
        setSiteBuild('');
    };
  }, [riserCount]);

  const offsetDate = (date) => {
    return date ? new Date(date.getTime() - (date.getTimezoneOffset() * 60000)) : null;
  }
  
  return (
    <TableContainer 
      component={Paper}
    >
      <Table
        style={{ 
          borderCollapse: 'collapse', 
          ...externalBorderStyle,
          minHeight: type === 'ED' ? '590px' : '350px',
          maxHeight: type === 'ED' ? '590px' : '350px',
          overflow: 'auto',
        }} 
        sx={{
          '& .MuiTableCell-root': {
            padding: '4px', 
            fontSize: '0.875rem',
            height: '33px',
            backgroundColor: 'inherit',
          },
          '& .MuiTableCell-head': {
            fontWeight: 'bold',
            backgroundColor: 'inherit',
          },
          '& .MuiTableRow-root': {
            height: '24px', 
          },
          '& th, & td': {
            whiteSpace: 'nowrap',
            textAlign: 'center', 
          },
          '&:hover': { backgroundColor: theme.palette.mode === 'light' ? '#F3F3F3' : '#252525' }
        }}
      >
        
        <TableHead>
          <TableRow>
            <TableCell colSpan={3} align="center" style={{ ...borderStyle, fontWeight: 'bold' }}>
              <Box display="inline-flex" alignItems="center" justifyContent="space-around" width="auto" marginLeft='15%'>
                <Typography variant='h6' gutterBottom style={{ fontFamily: "'Roboto Mono', monospace", fontWeight: 'bold', fontSize: '27px', paddingRight: userCanEdit ? null : '25%' }}>
                  Internal Information
                </Typography>
              {!isLoading && internalInfo && (
                userCanEdit ?
                <Box ml={2}>
                  <EditInternalInfo 
                    internInfo={internalInfo} 
                    onUpdateAll={handleInfoUpdate}
                    updateTz={fetchTimeZone}
                    prog={programmer}
                    ft={installTester}
                    cstation={siteInfoData.centralStation}
                    tt={timerTest}
                    tzone={timeZone}
                    csId={internalInfo.csId}
                  />
                </Box>
                : null
              )}
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        
        <TableBody>
          <TableRow>
            <TableCell style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>Programmer:</TableCell>
            <TableCell colSpan={2} align="center" style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>{programmer}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>Project Manager:</TableCell>
            <TableCell colSpan={2} align="center" style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>
              {prodSummaryData.projectManager}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>Install Tester:</TableCell>
            <TableCell colSpan={2} align="center" style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>
              {installTester}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>Site Build:</TableCell>
            <TableCell colSpan={2} align="center" style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>
              {siteBuild}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>Ship Date:</TableCell>
            <TableCell colSpan={2} align="center" style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>
              {internalInfo.shipDate ? (
                offsetDate(new Date(internalInfo.shipDate)).toLocaleString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                })
              ) : (
                ""
              )}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>Install Date:</TableCell>
            <TableCell colSpan={2} align="center" style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>
              {internalInfo.installDate ? (
                offsetDate(new Date(internalInfo.installDate)).toLocaleString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                })
              ) : (
                ""
              )}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>Monitoring Start Date:</TableCell>
            <TableCell colSpan={2} align="center" style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>
              {internalInfo.activationStartDate}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>Central Station:</TableCell>
            <TableCell colSpan={2} align="center" style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>
              {siteInfoData.centralStation}
              </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>Time Zone:</TableCell>
            <TableCell colSpan={2} align="center" style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>
              {timeZone && timeZone !== 'Unknown' ? timeZone : ''}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>Timer Test:</TableCell>
            <TableCell colSpan={2} align="center" style={borderStyle} sx={{ fontFamily: "'Roboto Mono', monospace" }}>
              {timerTest}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

